import { Box, Divider, Typography } from "@mui/material"
import type { FC } from "react"
import { useCallback } from "react"

import type { PayFileSearchParams } from "../../types/searchParams"

import { PayFileStatusFilter } from "./PayFileStatusFilter"
import { PayFileTypeFilter } from "./PayFileTypeFilter"

import { DateRangeFilter } from "@/components/common/Filters/DateRangeFilter"
import { SearchFilterDrawer } from "@/components/common/SearchFilterDrawer/SearchFilterDrawer"

interface PayFileFilterDrawerProps {
  open: boolean
  onClose: () => void
  searchParams: PayFileSearchParams
  onChange: <K extends keyof PayFileSearchParams>(key: K, value: PayFileSearchParams[K]) => void
  onBatchChange: (updates: Partial<PayFileSearchParams>) => void
  onClear: () => void
}

export const PayFileFilterDrawer: FC<PayFileFilterDrawerProps> = ({
  open,
  onClose,
  searchParams,
  onChange,
  onBatchChange,
  onClear,
}) => {
  const { type, scheduledPayDateFrom, scheduledPayDateTo } = searchParams

  const handleRangeChange = useCallback(
    (range: { from: string | undefined; to: string | undefined }) =>
      onBatchChange({ scheduledPayDateFrom: range.from, scheduledPayDateTo: range.to }),
    [onBatchChange]
  )
  const statuses = searchParams.statuses ?? []

  return (
    <SearchFilterDrawer open={open} toggleDrawer={onClose} clear={onClear} testId='pay-file.filter-drawer'>
      <Box className='h-full min-w-80'>
        <Box className='flex flex-1 flex-col'>
          <Typography variant='subtitle1' className='px-4 py-1.5 font-bold text-gray-700'>
            Type of File
          </Typography>
          <Divider />
          <PayFileTypeFilter type={type} onChange={(value) => onChange("type", value)} />
        </Box>

        <Box className='flex flex-1 flex-col'>
          <Typography variant='subtitle1' className='px-4 py-1.5 font-bold text-gray-700'>
            Status
          </Typography>
          <Divider />
          <PayFileStatusFilter statuses={statuses} onChange={(values) => onChange("statuses", values)} />
        </Box>

        <Box className='flex flex-1 flex-col'>
          <Typography variant='subtitle1' className='px-4 py-1.5 font-bold text-gray-700'>
            Date Range
          </Typography>
          <Divider />
          <DateRangeFilter
            from={scheduledPayDateFrom}
            to={scheduledPayDateTo}
            onRangeChange={handleRangeChange}
            required
            fromTestId='pay-file.filter-drawer.scheduled-pay-date-from-field'
            toTestId='pay-file.filter-drawer.scheduled-pay-date-to-field'
          />
        </Box>
      </Box>
    </SearchFilterDrawer>
  )
}
